<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

type UsernameCandidate = string | { username: string }

const props = withDefaults(defineProps<{
  modelValue: string
  candidates: UsernameCandidate[]
  placeholder?: string
  disabled?: boolean
  autocomplete?: string
}>(), {
  placeholder: '',
  disabled: false,
  autocomplete: 'off',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  submit: [value: string]
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const suggestionLeft = ref(0)
const cycleIndex = ref(0)
const suggestionActive = ref(false)

const candidateNames = computed(() => {
  const seen = new Set<string>()
  return props.candidates
    .map(candidate => typeof candidate === 'string' ? candidate : candidate.username)
    .map(candidate => candidate.trim())
    .filter(candidate => {
      const key = candidate.toLocaleLowerCase()
      if (!candidate || seen.has(key)) return false
      seen.add(key)
      return true
    })
})

const matchingCandidates = computed(() => {
  const query = props.modelValue.trim().toLocaleLowerCase()
  if (!query) return []
  return candidateNames.value.filter(candidate => (
    candidate.toLocaleLowerCase().startsWith(query)
    && candidate.toLocaleLowerCase() !== query
  )).slice(0, 8)
})

const activeCandidate = computed(() => {
  const matches = matchingCandidates.value
  return matches.length ? matches[Math.min(cycleIndex.value, matches.length - 1)] : ''
})

const suggestion = computed(() => {
  if (!suggestionActive.value) return ''
  const candidate = activeCandidate.value
  const input = props.modelValue
  return candidate.slice(input.length)
})

function measureSuggestion(): void {
  const input = inputRef.value
  if (!input) return
  const style = window.getComputedStyle(input)
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return
  context.font = style.font
  suggestionLeft.value = Number.parseFloat(style.borderLeftWidth || '0')
    + Number.parseFloat(style.paddingLeft || '0')
    + context.measureText(props.modelValue).width
}

function updateValue(value: string): void {
  cycleIndex.value = 0
  suggestionActive.value = false
  emit('update:modelValue', value)
}

function acceptSuggestion(withSpace = false): string {
  const value = activeCandidate.value || props.modelValue.trim()
  const accepted = value + (withSpace && value ? ' ' : '')
  if (activeCandidate.value) emit('update:modelValue', accepted)
  cycleIndex.value = 0
  suggestionActive.value = false
  void nextTick(() => {
    inputRef.value?.setSelectionRange(accepted.length, accepted.length)
    measureSuggestion()
  })
  return accepted
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    cycleIndex.value = 0
    suggestionActive.value = false
    return
  }
  if (event.key === 'Tab' && matchingCandidates.value.length) {
    event.preventDefault()
    if (!suggestionActive.value) {
      suggestionActive.value = true
      cycleIndex.value = event.shiftKey ? matchingCandidates.value.length - 1 : 0
    } else if (event.shiftKey && matchingCandidates.value.length > 1) {
      cycleIndex.value = (cycleIndex.value - 1 + matchingCandidates.value.length) % matchingCandidates.value.length
    } else if (matchingCandidates.value.length > 1) {
      cycleIndex.value = (cycleIndex.value + 1) % matchingCandidates.value.length
    }
    return
  }
  if (event.key === 'ArrowRight' && suggestionActive.value) {
    event.preventDefault()
    acceptSuggestion()
    return
  }
  if (event.key === ' ' && suggestionActive.value) {
    event.preventDefault()
    acceptSuggestion(true)
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    emit('submit', acceptSuggestion())
  }
}

watch(() => props.modelValue, () => void nextTick(measureSuggestion))
watch(candidateNames, () => {
  cycleIndex.value = 0
  suggestionActive.value = false
  void nextTick(measureSuggestion)
})
onMounted(measureSuggestion)

defineExpose({ focus: () => inputRef.value?.focus(), acceptSuggestion })
</script>

<template>
  <span class="username-ghost-input">
    <input
      ref="inputRef"
      :value="modelValue"
      class="username-ghost-field"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      :autocomplete="autocomplete"
      aria-autocomplete="inline"
      spellcheck="false"
      @input="updateValue(($event.target as HTMLInputElement).value)"
      @keydown="onKeydown"
    />
    <span v-if="suggestion" class="username-ghost-suggestion" :style="{ left: `${suggestionLeft}px` }">{{ suggestion }}</span>
  </span>
</template>

<style scoped>
.username-ghost-input { position:relative; min-width:0; width:100%; display:block; }
.username-ghost-field { width:100%; min-width:0; max-width:none; box-sizing:border-box; }
.username-ghost-suggestion { position:absolute; z-index:2; top:0; bottom:0; display:flex; align-items:center; color:#65717c; font:inherit; white-space:pre; pointer-events:none; }
</style>
